/**
 * Der Rahmen der angemeldeten Anwendung — die `App` aus
 * `ui_kits/dashboard/index.html`.
 *
 * Er hält zwei Dinge fest: welcher **Bereich** gerade zu sehen ist und welche
 * **Anlage** gilt. Alles andere liegt in den Bereichen daneben.
 *
 * **Die Seitenleiste führt ausschließlich die Anlage.** Jeder Eintrag darin
 * bezieht sich auf die im Umschalter darüber gewählte Anlage — der Umschalter
 * steht deshalb über der Navigation und nicht daneben. Was dem *Konto* gehört
 * und nicht der Anlage, hängt am Fuß der Leiste im Kontomenü: Profil,
 * Feedback, abmelden — und für den Plattform-Admin der Weg in den
 * **Admin-Bereich** (`/admin/…`), der eine eigene Leiste hat. Stünde beides in derselben
 * Liste, hieße „Einstellungen" mal das eine und mal das andere.
 *
 * **Die Anlagen selbst gehören in keine der beiden Listen, sondern in den
 * Umschalter.** Welche es gibt und dass eine dazukommen soll, fragt man dort,
 * wo man sie auswählt — nicht unten beim Konto. Der Bereich `sites` bleibt, aber
 * er wird von oben erreicht.
 *
 * **Beides steht in der Adresse** (`lib/router.ts`), nicht in einem `useState`:
 * `/anlagen/{id}/uebersicht`. Ein Neuladen kommt deshalb dorthin zurück, wo man
 * war, jeder Bereich ist verlinkbar, und die Zurück-Taste geht einen Schritt
 * zurück statt aus der Anwendung heraus.
 *
 * **Der Rahmen ist die einzige Stelle, die die Adresse zurechtrücken darf.** Nur
 * er kennt die Anlagen: ob die Kennung im Pfad noch eine ist, ob es überhaupt eine
 * gibt. Deshalb liest er die Route, entscheidet daraus, was zu sehen ist, und
 * schreibt das Ergebnis mit `replace` zurück — nicht umgekehrt.
 *
 * **Die Navigation zeigt nur, was es gibt.** Das Kit führt Heizung, Wasser und
 * Meldungen — die ersten beiden sind Sparten, die nicht umgesetzt sind, für die
 * dritte gibt es keinen Endpunkt. Das Kit sieht das selbst vor: Seine `Sidebar`
 * nimmt eine Liste der vorhandenen Kanäle entgegen und blendet den Rest aus. Was
 * eine Datenquelle nicht kann, wird ausgeblendet und nicht ausgegraut (Regel 10).
 */
import { useEffect, useState } from "react";
import { AppShell, Badge, Button, Card, EmptyState, NavItem, NavSection, Wordmark, icons } from "@hausmeter/ui";
import { logout, type CurrentUser } from "../lib/session.js";
import {
  useDevice,
  useDevices,
  useHealth,
  useMeter,
  useSiteArea,
  useGenerationAsset,
  useSiteAreas,
  useSiteChargePoints,
  useMyUnits,
  useSiteEnergy,
  useSharedSites,
  useSites,
  useUnitChargePoints,
  useUnitEnergy,
} from "../lib/queries.js";
import {
  isAdminArea,
  isSiteSection,
  navigate,
  routeFor,
  routePath,
  useRoute,
  withSite,
  type Area,
  type EnergyPeriod,
  type Route,
} from "../lib/router.js";
import { displayName, initialsOf } from "../lib/format.js";
import { deviceHeadline } from "../lib/device-labels.js";
import { meterHeadline } from "../lib/meter-labels.js";
import { unitHeadline } from "../lib/unit-labels.js";
import { useSidebarRail } from "../lib/sidebar-rail.js";
import { AccountMenu, type AccountMenuEntry } from "../components/account-menu.js";
import { LegalFooterLinks } from "../components/legal-layout.js";
import { LEGAL_META, LegalDocumentScreen } from "./legal.js";
import { SiteSwitcher } from "../components/site-switcher.js";
import { SiteAccessContext, sharedSiteLabel, siteAccessOf } from "../lib/site-access.js";
import { AdminCard } from "./admin.js";
import { AdminSitesCard } from "../components/admin-sites.js";
import { DatapointDetailScreen } from "./datapoint-detail.js";
import { DeviceDetailScreen } from "./device-detail.js";
import { MeterDetailScreen } from "./meter-detail.js";
import { MetersScreen } from "./meters.js";
import { DeviceManagementScreen } from "./device-management.js";
import { AppearancePicker } from "../components/appearance-picker.js";
import { PeriodControls } from "../components/period-controls.js";
import { ConceptScreen } from "./concept.js";
import { ElectricityScreen } from "./electricity.js";
import { ProfileScreen } from "./profile.js";
import { platformRoleLabel } from "../lib/platform-role.js";
import { SettingsScreen } from "./settings.js";
import { SitesCard } from "./sites.js";
import { FleetScreen } from "./fleet.js";
import { SystemScreen } from "./system.js";
import { UsageScreen } from "./usage.js";
import { FeedbackScreen } from "./feedback.js";
import { FeedbackDialog } from "../components/feedback-dialog.js";
import { NewsDialog } from "../components/news-dialog.js";
import { dueNews } from "../lib/news.js";
import { usageScreenOf, useUsageTracking } from "../lib/usage.js";
import { ResidentUnitScreen } from "./resident-unit.js";
import { ChargingScreen, ResidentChargingScreen } from "./charging.js";
import { UnitDetailScreen } from "./unit-detail.js";
import { UnitsScreen } from "./units.js";
import { GenerationAssetsScreen, generationAssetHeadline } from "./generation-assets.js";
import { GenerationAssetDetailScreen } from "./generation-asset-detail.js";

interface AreaMeta {
  readonly title: string;
  readonly subtitle: string;
}

/**
 * Titel und Unterzeile je Bereich, wie das `META`-Objekt im Kit.
 *
 * Die Unterzeilen beschreiben, was der Bereich **ist** — nicht, was er gerade
 * zeigt. „Ihr Zuhause in Echtzeit" aus dem Kit wäre heute eine Behauptung.
 *
 * **Ob ein Bereich der Anlage oder dem Konto gehört, steht hier nicht mehr.** Das
 * sagt sein Typ, und `isSiteSection` fragt ihn (`lib/router.ts`) — dieselbe Auskunft
 * zweimal zu führen, hieß, sie irgendwann in zwei Fassungen zu haben.
 */
const AREAS: Record<Area, AreaMeta> = {
  electricity: { title: "Strom & Erzeugung", subtitle: "Verbrauch, Erzeugung und Netzaustausch" },
  charging: { title: "Laden", subtitle: "Wie und bis wie voll geladen wird" },
  meters: { title: "Zähler", subtitle: "Zähler, Zählwerke und ihre Quellen" },
  units: { title: "Wohneinheiten", subtitle: "Wohnungen, Einheiten und ihre Messstellen" },
  generation: { title: "Erzeugungsanlagen", subtitle: "PV-Anlagen und Speicher hinter den Zählern" },
  concept: { title: "Messkonzept", subtitle: "Wie diese Anlage aus Zählerständen rechnet" },
  devices: { title: "Geräteverwaltung", subtitle: "Verbundene Geräte und Datenquellen" },
  settings: { title: "Einstellungen", subtitle: "Allgemein, Anschrift, Zugriff und Geräte" },
  /* Unterzeile nah an der Vorlage („Profil, Zugang und Darstellung dieses
     Kontos") — ohne die Darstellung, die es hier nicht zu wählen gibt. */
  profile: { title: "Profil", subtitle: "Name, Bild, Zugang und Konto" },
  sites: { title: "Anlagen", subtitle: "Anlagen des Kontos und Kontingent" },
  system: { title: "System", subtitle: "Zustand der Dienste" },
  admin: { title: "Anlagen und Nutzer", subtitle: "Anlagen, Teilungen, Konten und Plattform-Rollen" },
  fleet: { title: "Geräte", subtitle: "Alle Geräte der Plattform und ob sie sich melden" },
  usage: { title: "Nutzung", subtitle: "Ansichten und Funktionen in Web-App und Apps" },
  feedback: { title: "Feedback", subtitle: "Fehlermeldungen und Wünsche aus Web-App und Apps" },
};

/**
 * Titel und Unterzeile für den Bereich — mit einer Ausnahme.
 *
 * Wer noch keine Anlage hat, liest über einem leeren Formular nicht „Anlagen ·
 * Anlagen des Kontos und Kontingent". Das beschreibt eine Liste, die es nicht
 * gibt, und lässt offen, was jetzt zu tun ist. Der Anfang bekommt deshalb seine
 * eigene Überschrift.
 */
function metaFor(area: Area, withoutSite: boolean): AreaMeta {
  if (area === "sites" && withoutSite) {
    return {
      title: "Erste Anlage",
      subtitle: "Ein Haus, eine Wohnung, eine Anlage — ohne sie hat HausMeter nichts zu messen",
    };
  }

  return AREAS[area];
}

export function DashboardPage({ user }: { user: CurrentUser }) {
  const route = useRoute();

  /**
   * Die Nutzungsstatistik (HM-168): Jede Ansicht dieser Sitzung wird unter
   * ihrem Schlüssel gezählt — hier, weil nur der Rahmen jede Route sieht.
   * Abgewählt im Profil, wird nichts gesammelt und nichts gesendet.
   */
  useUsageTracking(route, user.usageAnalytics);

  /**
   * Ob die Seitenleiste als Schiene steht (HM-070).
   *
   * **Der Zustand gehört hierher und nicht in `AppShell`**: Er soll ein
   * Neuladen überleben, und wo das geschieht, entscheidet die Anwendung
   * (`lib/sidebar-rail.ts`) — nicht das Gerüst, das auch in einer Mobile-App
   * stehen könnte, die kein `localStorage` hat.
   */
  const rail = useSidebarRail();

  /**
   * Kam der Nutzer über den Kopplungslink eines Geräts (`…/verbinden?code=…`),
   * beginnt er in der Geräteverwaltung — mit dem Code schon im Feld. Alles
   * andere wäre unhöflich: Er hat gerade einen Link geöffnet, um genau das zu
   * tun, und stünde sonst vor „Strom & Erzeugung".
   *
   * Der Code lebt im Zustand und nicht in der Route, weil die Adresse gleich
   * darauf zurechtgerückt wird: Ein Neuladen führte sonst erneut hierher, mit
   * einem längst verbrauchten Code. `useState(...)` mit Funktion, damit er
   * **einmal** gelesen wird und nicht bei jedem Neuzeichnen.
   */
  const [claimCode, setClaimCode] = useState(() => (route.kind === "claim" ? route.code : null));

  /**
   * Was die Adresse verlangt. `start` ist der Aufruf der Wurzel und alles, was
   * nicht zu deuten war — „Strom & Erzeugung" ist dann der Anfang, nicht die
   * Deutung. **Eine Übersicht gibt es nicht mehr** (HM-156): Umgesetzt ist eine
   * Sparte, und die Übersicht stellte dieselbe Frage an dasselbe Messkonzept
   * wie „Strom & Erzeugung", nur knapper — zwei Seiten, von denen die eine die
   * andere enthielt.
   */
  const chosenArea: Area =
    route.kind === "site" || route.kind === "account"
      ? route.area
      : route.kind === "usage"
        ? "usage"
      : route.kind === "feedback"
        ? "feedback"
      : route.kind === "fleet"
        ? "fleet"
      : route.kind === "claim" || route.kind === "device" || route.kind === "datapoint"
        ? "devices"
        : route.kind === "meter"
          ? "meters"
          : route.kind === "unit"
            ? "units"
          : route.kind === "generation-asset"
            ? "generation"
          : route.kind === "charging"
            ? "charging"
            : "electricity";

  /**
   * Welches Gerät geöffnet ist — `null` heißt: die Liste.
   *
   * Die Detailseite ist **kein eigener Bereich**: In der Seitenleiste bleibt
   * „Geräteverwaltung" hervorgehoben, und die Kopfzeile behält ihren Titel. Sie
   * ist eine Ebene tiefer im selben Bereich, und genau so liest sich auch ihre
   * Adresse.
   */
  const openDevice = route.kind === "device" ? route : null;
  /**
   * **Und eine Ebene darunter der Datenpunkt** (HM-053). Er liegt im selben
   * Bereich und unter demselben Gerät; für die Seitenleiste ist beides
   * „Geräteverwaltung".
   */
  const openPoint = route.kind === "datapoint" ? route : null;
  const openDeviceId = openDevice?.deviceId ?? openPoint?.deviceId ?? null;
  /**
   * Und welcher Reiter darin — er gehört zur Adresse des Geräts und muss beim
   * Zurechtrücken mitgeführt werden, sonst spränge jeder Klick auf „Aktionen"
   * im selben Augenblick zurück auf die Übersicht.
   */
  const openDeviceTab = openDevice?.tab ?? "overview";

  /**
   * **Und im Zähler-Bereich dasselbe eine Ebene tiefer** (HM-057): Ein
   * geöffneter Zähler ist kein eigener Bereich, sondern eine Ansicht unter
   * „Zähler" — in der Seitenleiste bleibt der Eintrag hervorgehoben.
   */
  const openMeter = route.kind === "meter" ? route : null;
  /**
   * **Die Wohnung eines Bewohners** (HM-081) — weder Anlage noch Konto, wie die
   * Rechtsseiten ein dritter Fall: kein Eintrag der Leiste leuchtet, das
   * Zurechtrücken hört auf, die Unterzeile nennt keine Anlage.
   */
  const residentUnit = route.kind === "resident-unit" ? route : null;
  /** **Und „Laden" in der Wohnung** (HM-119) — derselbe dritte Fall, eine Ansicht weiter. */
  const residentCharging = route.kind === "resident-charging" ? route : null;
  /** Die Wohnung aus der Adresse, gleich welche ihrer Ansichten offen ist. */
  const residentUnitId = residentUnit?.unitId ?? residentCharging?.unitId ?? null;
  /** Der gewählte Ladepunkt auf „Laden" der Anlage — er wird beim Zurechtrücken mitgeführt. */
  const openCharging = route.kind === "charging" ? route : null;
  /** **Und eine geöffnete Wohneinheit** (HM-079) — vierter Zweig derselben Regel. */
  const openUnit = route.kind === "unit" ? route : null;
  /** **Und eine geöffnete Erzeugungsanlage** (HM-131) — fünfter Zweig derselben Regel. */
  const openAsset = route.kind === "generation-asset" ? route : null;
  /* Zeitraum und Plattform der Nutzungsstatistik gehören zur Adresse (HM-168). */
  const openUsage = route.kind === "usage" ? route : null;
  /* Filter und geöffnete Meldung des Feedbacks gehören zur Adresse (HM-169). */
  const openFeedback = route.kind === "feedback" ? route : null;
  /* Zustand und Typ der Geräteübersicht gehören zur Adresse (#89). */
  const openFleet = route.kind === "fleet" ? route : null;
  /**
   * „Feedback senden" ist ein Dialog über der Ansicht, die gerade offen ist,
   * und keine Adresse: Die Ansicht geht als Schlüssel mit, und wer den Dialog
   * schließt, steht wieder dort, wo er war (HM-169).
   */
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  /**
   * Neuigkeiten einmal nach dem Anmelden (HM-170). Ob sie fällig sind, sagt das
   * Konto; `newsClosed` hält sie zu, bis die Antwort auf das Speichern da ist.
   */
  const [newsClosed, setNewsClosed] = useState(false);
  const news = newsClosed ? null : dueNews(user);
  /* Zeitraum und Datum von „Strom & Erzeugung" — sie gehören zur Adresse und
     werden beim Zurechtrücken mitgeführt wie der Reiter eines Zählers (HM-012,
     HM-076). */
  const energyView =
    route.kind === "electricity" ? { period: route.period, date: route.date } : null;

  /**
   * Die Anlage steht im Pfad; an einem Konto-Bereich hängt sie als `?anlage=`.
   * Der Kopplungslink kennt keine — welche es wird, entscheidet erst das
   * Formular.
   */
  const requestedSiteId =
    route.kind === "site" ||
    route.kind === "electricity" ||
    route.kind === "charging" ||
    route.kind === "device" ||
    route.kind === "datapoint" ||
    route.kind === "meter" ||
    route.kind === "generation-asset" ||
    route.kind === "unit"
      ? route.siteId
      : route.kind === "account" || route.kind === "usage" || route.kind === "feedback" || route.kind === "fleet"
        ? route.siteId
        : null;

  /**
   * **Ein reiner Bewohner hat keine Anlage und keine Navigation** (HM-081). Wer
   * in keinem Account eine Rolle mit `site:read` hat, bekommt eine Hülle ohne
   * Umschalter: oben seine Wohnung(en), sonst nichts. Die Entscheidung fällt
   * an der Rolle aus `GET /auth/me`, **bevor** `useSites` läuft — für ihn
   * antwortete es mit `403`, und ein Fehler vor der ersten Ansicht wäre sein
   * erster Eindruck.
   */
  /**
   * **Geteilte Anlagen stehen neben den eigenen** (HM-160, ADR 0043). Wer in
   * keinem Account eine Rolle außer `resident` und `guest` hat, führt keine
   * eigene Anlage — `useSites` antwortete ihm mit `403`. Ist ihm aber eine
   * geteilt, bekommt er den gewöhnlichen Rahmen mit dieser Anlage und nicht die
   * Hülle eines Bewohners. Bis die Teilungen geladen sind, gilt ein reiner Gast
   * nicht als Bewohner: Die Hülle blitzte sonst einen Augenblick auf.
   */
  const ownsSites = user.accounts.some((account) => account.role !== "resident" && account.role !== "guest");
  const isGuest = user.accounts.some((account) => account.role === "guest");
  const sharedSites = useSharedSites();
  const shared = sharedSites.data?.sites ?? [];
  const sharedKnown = sharedSites.data !== undefined || sharedSites.isError;
  const residentOnly =
    user.accounts.length > 0 && !ownsSites && (sharedKnown ? shared.length === 0 : !isGuest);
  const myUnits = useMyUnits();
  const units = myUnits.data?.units ?? [];
  /**
   * **Die Wohnung, der die Leiste gehört** (HM-085): die aus der Adresse — und
   * für einen reinen Bewohner auf einem Konto-Bereich seine erste, so wie für
   * einen Besitzer über dem Profil die Anlage stehen bleibt. Ohne das war die
   * Leiste auf dem Profil leer, obwohl der Umschalter eine Wohnung zeigte.
   */
  const sidebarUnitId = residentUnitId ?? (residentOnly ? (units[0]?.siteAreaId ?? null) : null);

  const sites = useSites(ownsSites);
  const health = useHealth();

  const isAdmin = user.platformRole === "admin";

  /**
   * Nur aktive Anlagen stehen zur Auswahl. Eine archivierte auszuwählen, hieße
   * eine Ansicht auf etwas zu öffnen, das ausdrücklich stillgelegt wurde.
   */
  const ownActiveSites = sites.data?.items.filter((site) => site.status === "active") ?? [];
  const activeSites = [
    ...ownActiveSites,
    ...shared.map((entry) => entry.site).filter((site) => !ownActiveSites.some((own) => own.id === site.id)),
  ];
  const sharedLabels = new Map(shared.map((entry) => [entry.site.id, sharedSiteLabel(entry)]));
  /**
   * Die Kennung aus der Adresse gilt nur, wenn sie zu einer aktiven Anlage
   * gehört. Sonst die erste — eine Adresse kann von gestern sein, aus einer
   * Lesezeichenleiste stammen oder auf eine inzwischen archivierte Anlage
   * zeigen; sie wird gleich darauf zurechtgerückt.
   */
  const currentSite =
    activeSites.find((site) => site.id === requestedSiteId) ?? activeSites[0] ?? null;

  /**
   * **Nach** der Wahl der Anlage: Geräte gehören ihr (ADR 0017), und ohne sie
   * gibt es nichts zu holen. Ein Wechsel im Umschalter lädt die Liste neu — der
   * Anlagenbezug steht im Abfrageschlüssel.
   *
   * Die **Integrationen** holt der Rahmen seit HM-050 nicht mehr: Er zeigte sie
   * nirgends, sondern zählte sie nur in die Zahl an „Geräteverwaltung" hinein.
   * Wer sie braucht, ist die Geräteverwaltung selbst, und die holt sie über
   * denselben Abfrageschlüssel.
   */
  const devices = useDevices(currentSite?.id ?? null);

  /**
   * **Solange die Anlagen nicht geladen sind, wird nichts behauptet.** Ohne diese
   * Unterscheidung sähe der erste Augenblick nach dem Anmelden aus wie „Sie haben
   * keine Anlage" — die Leiste zeigte den Anlegen-Knopf und klappte eine
   * Sekunde später zur Navigation um.
   */
  const sitesKnown = (!ownsSites || sites.data !== undefined) && sharedKnown;
  const hasSite = currentSite !== null;

  /**
   * Ohne Anlage gibt es keinen Anlagen-Bereich, auf den man schauen könnte:
   * Strom, Zähler, Geräte und Einstellungen beziehen sich alle auf eine, die
   * es nicht gibt. Wer trotzdem dort landet — etwa weil er seine letzte Anlage
   * gerade archiviert hat —, wird zum Anlegen geführt statt vor vier leere
   * Seiten gestellt. Die Konto-Bereiche bleiben erreichbar; das Profil hängt an
   * keiner Anlage.
   */
  const area: Area = sitesKnown && !hasSite && isSiteSection(chosenArea) ? "sites" : chosenArea;

  /**
   * Das Rechtsdokument, falls die Adresse auf eines zeigt (HM-061).
   *
   * **Es ist kein `Area`** (siehe `lib/router.ts`): Der Schnitt der
   * Seitenleiste kennt Anlage und Konto, und ein Impressum gehört keinem von
   * beiden. Es steht deshalb neben dem Bereich und nicht in ihm — und
   * `navArea` sorgt dafür, dass währenddessen **kein** Eintrag der Leiste
   * hervorgehoben ist. Ohne das leuchtete „Strom & Erzeugung" auf, weil `chosenArea`
   * für alles Undeutbare dorthin zeigt; die Leiste behauptete dann, man stünde
   * in einem Bereich, den man gerade verlassen hat.
   */
  const legalDocument = route.kind === "legal" ? route.document : null;
  const navArea: Area | null = legalDocument === null && residentUnitId === null ? area : null;
  /**
   * **Der Admin-Bereich hat eine eigene Leiste.** System, Anlagen und Nutzer,
   * Nutzung und Feedback gehören weder der gewählten Anlage noch dem eigenen
   * Konto, sondern der Installation — als vier Einträge im Kontomenü wuchs
   * dort eine zweite Navigation heran. Steht man in einem davon, zeigt die
   * Leiste die Admin-Bereiche statt der Anlage, wie sie bei einer Wohnung deren
   * Ansichten zeigt; ein Eintrag oben führt zurück. Nur für den Plattform-Admin
   * — für jeden anderen bleibt die Seite ohnehin leer, und die Leiste mit ihr
   * bei der Anlage.
   */
  const adminView = isAdmin && navArea !== null && isAdminArea(navArea);


  /**
   * **Die Adresse folgt dem, was zu sehen ist.** Sie wird ergänzt, sobald die
   * Anlagen bekannt sind: `/` wird zu „Strom & Erzeugung" einer Anlage, eine erloschene
   * Kennung durch eine gültige ersetzt, der Kopplungslink durch die
   * Geräteverwaltung. Vorher wäre jedes Zurechtrücken geraten — es stünde nicht
   * fest, ob es überhaupt eine Anlage gibt.
   *
   * Immer `replace`: Der Nutzer ist diese Zwischenschritte nicht gegangen, und
   * die Zurück-Taste soll ihn nicht auf eine Adresse führen, die im selben
   * Augenblick wieder ersetzt würde. `navigate` tut nichts, wenn die Adresse
   * bereits stimmt — das ist der Normalfall nach dem ersten Mal.
   *
   * **Solange die Liste neu geladen wird, wird nichts zurechtgerückt.** Wer
   * gerade seine erste Anlage angelegt hat, steht für einen Augenblick auf
   * `/anlagen/{neu}/strom`, während die Liste sie noch nicht führt — die
   * Adresse wäre dann „ungültig" und würde ausgerechnet dorthin zurückgeschrieben,
   * wo er hergekommen ist.
   */
  useEffect(() => {
    if (!sitesKnown || sites.isFetching) return;
    /**
     * **Eine Rechtsadresse ist vollständig und wird nicht zurechtgerückt.** Sie
     * trägt keine Anlage, weil sie keine braucht; ohne diesen Riegel bildete der
     * Rahmen `routeFor(area, …)` und schriebe `/impressum` im selben Augenblick
     * auf „Strom & Erzeugung" zurück — die Seite wäre aus dem Dashboard heraus nicht
     * zu öffnen. Dieselbe Regel wie beim geöffneten Gerät, nur eine Ebene höher.
     */
    if (legalDocument !== null || residentUnitId !== null) return;

    /**
     * **Ein geöffnetes Gerät bleibt geöffnet.** Ohne diesen Zweig schriebe der
     * Rahmen die Adresse im selben Augenblick auf `/anlagen/{id}/geraete`
     * zurück — die Detailseite wäre nicht aufrufbar, nicht verlinkbar und nach
     * jedem Neuladen wieder weg. Die Anlage stammt dabei aus `currentSite` und
     * nicht aus dem Pfad: Zeigt die Adresse auf eine erloschene Anlage, wird sie
     * ersetzt, und das Gerät wandert mit — ob es dorthin gehört, entscheidet
     * dann die Detailseite, die als Einzige das Gerät kennt.
     */
    /**
     * **Ein geöffnetes Gerät bleibt geöffnet — und ein geöffneter Datenpunkt
     * auch.** `withSite` schreibt nur die Anlage um und lässt die Ansicht
     * stehen; ohne das bildete der Rahmen `routeFor("devices", …)` und schriebe
     * die Detailadresse im selben Augenblick auf die Liste zurück.
     */
    /**
     * **Ein geöffneter Zähler bleibt ebenso geöffnet** (HM-057) — dritter Zweig
     * derselben Regel. Wer einen Bereich um eine Detailebene erweitert, braucht
     * ihn: Ohne ihn bildete der Rahmen `routeFor("meters", …)` und schriebe die
     * Detailadresse im selben Augenblick auf die Liste zurück.
     */
    const target =
      area === "devices" && currentSite !== null && (openPoint !== null || openDeviceId !== null)
        ? withSite(
            openPoint ?? {
              kind: "device",
              siteId: currentSite.id,
              deviceId: openDeviceId!,
              tab: openDeviceTab,
            },
            currentSite.id,
          )
        : area === "meters" && currentSite !== null && openMeter !== null
          ? withSite(openMeter, currentSite.id)
          : area === "units" && currentSite !== null && openUnit !== null
            ? withSite(openUnit, currentSite.id)
          : area === "generation" && currentSite !== null && openAsset !== null
            ? withSite(openAsset, currentSite.id)
          : area === "electricity" && currentSite !== null && energyView !== null
            ? ({ kind: "electricity", siteId: currentSite.id, ...energyView } as const)
            : area === "charging" && currentSite !== null && openCharging !== null
              ? withSite(openCharging, currentSite.id)
              : area === "usage" && openUsage !== null
                ? { ...openUsage, siteId: currentSite?.id ?? null }
                : area === "feedback" && openFeedback !== null
                  ? { ...openFeedback, siteId: currentSite?.id ?? null }
                  : area === "fleet" && openFleet !== null
                    ? { ...openFleet, siteId: currentSite?.id ?? null }
                    : routeFor(area, currentSite?.id ?? null);

    navigate(target, { replace: true });
  }, [
    sitesKnown,
    sites.isFetching,
    legalDocument,
    area,
    currentSite?.id,
    openDeviceId,
    openDeviceTab,
    openPoint?.datapointId,
    openPoint?.range,
    openMeter?.meterId,
    energyView?.period,
    energyView?.date,
    /* Der Reiter gehört zur Adresse des Zählers und muss beim Zurechtrücken
       mitgeführt werden — sonst spränge jeder Klick auf „Einstellungen" im
       selben Augenblick zurück auf die Übersicht (dieselbe Falle wie beim
       Gerät, HM-042). */
    openMeter?.tab,
    openMeter?.register,
    openMeter?.range,
    openUnit?.unitId,
    openUnit?.tab,
    openUnit?.period,
    openUnit?.date,
    /* Anlage und Zeitraum ihres Verlaufs gehören zur Adresse (HM-131). */
    openAsset?.assetId,
    openAsset?.range,
    /* Der gewählte Ladepunkt gehört zur Adresse von „Laden" (HM-119). */
    openCharging?.chargePointId,
    openUsage?.days,
    openUsage?.platform,
    openUsage?.tab,
    openUsage?.grain,
    openFeedback?.feedbackId,
    openFeedback?.feedbackKind,
    openFeedback?.status,
    openFleet?.state,
    openFleet?.deviceType,
    residentUnitId,
  ]);

  const meta =
    legalDocument === null
      ? metaFor(area, sitesKnown && !hasSite)
      : /* Titel und Unterzeile kommen aus `LEGAL_META` — derselben Liste, aus
           der die eigenständige Fassung ihren Kartenkopf speist. Zwei Listen
           wären zwei Gelegenheiten, sie auseinanderlaufen zu lassen. */
        { title: LEGAL_META[legalDocument].title, subtitle: LEGAL_META[legalDocument].lead };

  /**
   * **Ein geöffneter Zähler stellt die Kopfleiste** (HM-072, aus
   * `ui_kits/dashboard/index.html`). Dort standen bis dahin „Zähler" und die
   * Beschreibung des Bereichs, während die Seite darunter noch einmal einen
   * Namen über einer Faktenzeile trug — dieselbe Form zweimal untereinander.
   *
   * **Derselbe Abfrageschlüssel wie auf der Detailseite** (`["meters",
   * "detail", id]`): Das ist kein zweiter Abruf, sondern derselbe Eintrag im
   * Zwischenspeicher. Solange er fehlt, bleibt es beim Bereichsnamen — ein
   * leerer Titel für einen Augenblick sähe aus, als lade die Seite nicht.
   */
  const openMeterData = useMeter(openMeter?.meterId ?? null).data;
  /* Nur, wenn er zu dieser Anlage gehört — sonst widerspräche die Kopfleiste
     der Seite darunter (Befund R-1), wie beim Gerät. */
  const meterHead =
    openMeter !== null && openMeterData !== undefined && openMeterData.siteId === currentSite?.id
      ? meterHeadline(openMeterData)
      : null;

  /**
   * **Und ein geöffnetes Gerät ebenso** (HM-073) — dieselbe Regel, derselbe
   * Zwischenspeicher (`["devices", "detail", id]`).
   *
   * **Nur für das Gerät selbst, nicht für einen Datenpunkt darunter.** Der hat
   * seinen eigenen Kopf auf der Seite und einen eigenen Weg zurück; stünde
   * hier der Gerätename, benennte die Kopfleiste eine Ebene, auf der man
   * gerade nicht steht.
   *
   * Und **nur, wenn das Gerät zu dieser Anlage gehört**: Sonst zeigt die Seite
   * darunter „gehört zu einer anderen Anlage", und die Kopfleiste trüge
   * trotzdem seinen Namen — die Behauptung, die die Seite gerade widerlegt
   * (ADR 0017).
   */
  const openDeviceData = useDevice(openDevice?.deviceId ?? null).data;
  const deviceHead =
    openDevice !== null && openDeviceData !== undefined && openDeviceData.siteId === currentSite?.id
      ? deviceHeadline(openDeviceData)
      : null;

  /**
   * **Und eine geöffnete Wohneinheit** (HM-079) — derselbe Zwischenspeicher
   * wie die Detailseite (`["site-areas", "detail", id]`), nur wenn sie zu
   * dieser Anlage gehört.
   */
  const openUnitData = useSiteArea(openUnit?.unitId ?? null).data;
  const unitHead =
    openUnit !== null && openUnitData !== undefined && openUnitData.siteId === currentSite?.id
      ? unitHeadline(openUnitData)
      : null;

  /**
   * **Und eine geöffnete Erzeugungsanlage** (HM-131) — derselbe Zwischenspeicher
   * wie die Detailseite (`["generation-assets", "detail", id]`), nur wenn sie
   * zu dieser Anlage gehört.
   */
  const openAssetData = useGenerationAsset(openAsset?.assetId ?? null).data;
  const assetHead =
    openAsset !== null && openAssetData !== undefined && openAssetData.siteId === currentSite?.id
      ? generationAssetHeadline(openAssetData)
      : null;

  const residentUnitData = residentUnitId === null ? null : (myUnits.data?.units.find((u) => u.siteAreaId === residentUnitId) ?? null);
  /* Die Wohnung steht seit HM-082 oben im Umschalter; der Kopf trägt wie bei
     jeder Anlagen-Ansicht den Bereich und darunter, worauf er sich bezieht. */
  const residentHead =
    residentUnitData === null
      ? null
      : residentCharging !== null
        ? { title: "Laden", subtitle: `${residentUnitData.unitName} · wie und bis wie voll geladen wird` }
      : residentUnit === null
        ? null
      : { title: "Strom", subtitle: `${residentUnitData.unitName} · Verbrauch, Herkunft, Leistung und Zähler` };

  /** Der Kopf einer Detailebene — höchstens einer ist gesetzt. */
  const detailHead = meterHead ?? deviceHead ?? unitHead ?? assetHead ?? residentHead;

  /**
   * **Der Eintrag „Wohneinheiten" steht nur, wo er etwas zeigt** (HM-079): an
   * einem Mehrfamilienhaus oder an einer Anlage, die schon Einheiten hat.
   * Ausgeblendet, nicht ausgegraut (Regel 10) — und **kein Schutz**: Die
   * Adresse bleibt erreichbar, und ein Einfamilienhaus darf Einheiten führen,
   * sobald jemand die erste anlegt.
   */
  const siteAreas = useSiteAreas(currentSite?.id ?? null);
  const showUnits =
    currentSite?.kind === "multi_family_house" ||
    (siteAreas.data?.items.length ?? 0) > 0 ||
    area === "units";
  const deviceCount = devices.data?.items.length ?? 0;

  /**
   * **„Laden" steht nur, wo es einen Ladepunkt gibt** (HM-119) — an der Anlage wie
   * in der Wohnung. Ausgeblendet, nicht ausgegraut (Regel 10), und kein Schutz:
   * Die Adresse bleibt erreichbar und erklärt dann, warum sie leer ist.
   */
  const siteChargePoints = useSiteChargePoints(residentOnly ? null : (currentSite?.id ?? null));
  const showCharging = (siteChargePoints.data?.items.length ?? 0) > 0 || area === "charging";
  const unitChargePoints = useUnitChargePoints(sidebarUnitId);
  const showUnitCharging = (unitChargePoints.data?.items.length ?? 0) > 0 || residentCharging !== null;

  /**
   * Ob noch eine Anlage dazukommen darf — die Bedingung für „Anlage
   * hinzufügen" im Umschalter. Hier nur zur **Anzeige**: Verbindlich entscheidet
   * der Server. Ohne die Bedingung führte der Eintrag ein Standardkonto zu einem
   * Formular, das mit `403` antwortet, und ein Menüpunkt ohne Wirkung wird
   * ausgeblendet, nicht ausgegraut (Regel 10).
   */
  // Gezählt werden nur die eigenen: Eine geteilte Anlage verbraucht kein Kontingent.
  const canAddSite = ownsSites && (user.siteQuota === null || ownActiveSites.length < user.siteQuota);

  /**
   * Ein Bereichswechsel ist ein Schritt, den der Nutzer selbst geht — also ein
   * Eintrag im Verlauf, zu dem die Zurück-Taste zurückführt. Die gewählte Anlage
   * kommt mit: Sie gilt weiter, auch wenn der Bereich sie nicht anzeigt.
   */
  function go(target: Area): void {
    navigate(routeFor(target, currentSite?.id ?? null));
  }

  const accountEntries: AccountMenuEntry[] = [
    {
      key: "profile",
      label: "Profil",
      icon: <icons.IconUser size={18} />,
      onSelect: () => go("profile"),
    },
    /**
     * **„Administration" nur für den Plattform-Admin**, und als **ein**
     * Eintrag: Er führt in den Admin-Bereich, dessen Leiste die einzelnen
     * Seiten trägt. Vorher standen sie hier einzeln, und mit jeder neuen
     * Admin-Seite wurde das Menü des eigenen Kontos länger. Das ist **kein**
     * Schutz, sondern Aufräumen: Die Endpunkte dahinter prüfen die Rolle selbst
     * und gegen die Datenbank.
     */
    ...(isAdmin
      ? [
          {
            key: "administration",
            label: "Administration",
            icon: <icons.IconShield size={18} />,
            onSelect: () => go("system"),
          },
        ]
      : []),
    /**
     * „Feedback senden" für jeden (HM-169) — **der einzige Einstieg**, wie im
     * Entwurf: kein schwebendes Symbol über jeder Seite. Es öffnet einen Dialog
     * und keinen Bereich; deshalb leuchtet der Eintrag nie.
     */
    {
      key: "send-feedback",
      label: "Feedback senden",
      icon: <icons.IconMessage size={18} />,
      onSelect: () => setFeedbackOpen(true),
    },
    /* Abgesetzt am Ende: Es verlässt die Anwendung, während alles darüber nur
       die Ansicht wechselt. */
    {
      key: "logout",
      label: "Abmelden",
      separated: true,
      onSelect: () => {
        void logout();
        /**
         * Zurück auf die Wurzel, und zwar mit `replace`: In der Adressleiste
         * stünde sonst weiter die Anlagenkennung des eben abgemeldeten Kontos,
         * und der Nächste, der sich hier anmeldet, käme über die Zurück-Taste
         * an eine Adresse, die ihn nichts angeht. Sie führte zwar nur zu seiner
         * eigenen ersten Anlage — aber lesen kann er sie trotzdem.
         */
        navigate({ kind: "start" }, { replace: true });
      },
    },
  ];

  return (
    /* Was der Nutzer an der gewählten Anlage darf (HM-160) — die Ansichten fragen
       es über `useSiteAccess` ab und blenden aus, was die Stufe nicht erlaubt. */
    <SiteAccessContext.Provider value={siteAccessOf(currentSite?.id ?? null, shared)}>
    <AppShell
      /**
       * Das echte Logo aus dem Design-System, nicht nachgezeichnet. Es liegt
       * unter `public/` und wird deshalb unverändert ausgeliefert — kein
       * Import, keine Umbenennung durch den Bundler, damit auch das Favicon in
       * `index.html` auf dieselbe Datei zeigen kann.
       *
       * `alt=""` ist Absicht: Der Schriftzug „HausMeter" steht direkt daneben.
       * Eine Sprachausgabe soll den Namen einmal lesen, nicht zweimal.
       *
       * **Darunter die Dachmarke** „by Christians Technikshop" — die Geräte
       * werden unter dieser Marke verkauft (HM-061). Ihr Zeichen ist ein
       * Ausschnitt des Original-Logos aus dem Design-System, ebenfalls nicht
       * nachgezeichnet. Die Größe des Logos rechnet `Wordmark` selbst: Es
       * schließt oben mit dem Schriftzug und unten mit dem Chip ab.
       */
      brand={
        <Wordmark
          endorsement
          logo={<img src="/logo.png" alt="" style={{ width: "100%", height: "100%" }} />}
          endorsementMark={<img className="hm-ct-mark" src="/ctshop-mark.png" alt="" style={{ height: "100%", width: "auto" }} />}
        />
      }
      /**
       * **Auf der Schiene nur das Zeichen.** Der Schriftzug passt auf 64 Pixel
       * nicht, und abgeschnitten wäre er schlechter als keiner. `alt` trägt
       * hier den Namen, anders als in `brand`: Dort steht er als Text daneben,
       * hier steht das Bild allein.
       */
      brandMark={<img src="/logo.png" alt="HausMeter" width={34} height={34} style={{ borderRadius: 9 }} />}
      collapsed={rail.collapsed}
      onToggleCollapse={rail.toggle}
      /**
       * **Die Adresse schließt die Schublade.** Auf einem schmalen Fenster liegt
       * die Seitenleiste über dem Inhalt; wer dort einen Bereich wählt, will ihn
       * sehen. Der Pfad ist dafür der richtige Schlüssel — er ist genau dann ein
       * anderer, wenn sich die Ansicht wirklich geändert hat, und bleibt gleich,
       * wenn jemand in der Leiste nur ein Menü aufklappt.
       */
      navKey={routePath(route)}
      /**
       * Oben in der Leiste steht, worauf sich alles darunter bezieht — und wenn
       * es das nicht gibt, steht dort der Weg dahin. Der Umschalter mit null
       * Einträgen wäre ein Bedienelement ohne Wirkung; ein Knopf sagt, was zu
       * tun ist.
       *
       * Solange die Anlagen noch geladen werden, bleibt die Stelle leer: Beides
       * zu zeigen hieße, sich innerhalb einer Sekunde zu widersprechen.
       */
      /**
       * **Und die Wohnungen stehen mit darin** (HM-082). Ein Bewohner wählt
       * oben seine Wohnung wie ein Besitzer seine Anlage; wer beides hat, sieht
       * beides in einem Menü. Für einen reinen Bewohner gilt statt der Anlagen
       * die Wohnungsliste als „bekannt" — `useSites` läuft für ihn nicht.
       */
      sidebarTop={
        adminView ? (
          <AdminHead />
        ) : residentOnly ? (
          units.length > 0 ? (
            <SiteSwitcher
              sites={[]}
              activeSiteId={null}
              onSelect={() => undefined}
              units={units}
              activeUnitId={sidebarUnitId}
              onSelectUnit={(unitId) => navigate({ kind: "resident-unit", unitId, section: "electricity", period: "day", date: null })}
            />
          ) : undefined
        ) : !sitesKnown ? undefined : hasSite ? (
          <SiteSwitcher
            sites={activeSites}
            activeSiteId={currentSite.id}
            /* Der Bereich bleibt stehen, nur die Anlage wechselt — wer in den
               Einstellungen steht und umschaltet, will die Einstellungen der
               anderen Anlage sehen und nicht wieder auf der Startseite landen.
               Aus einer Wohnung heraus führt der Wechsel auf „Strom &
               Erzeugung": Es gibt keinen Bereich, der stehen bleiben könnte. */
            onSelect={(id) => navigate(routeFor(residentUnitId === null ? area : "electricity", id))}
            units={units}
            activeUnitId={residentUnitId}
            onSelectUnit={(unitId) => navigate({ kind: "resident-unit", unitId, section: "electricity", period: "day", date: null })}
            onAddSite={canAddSite ? () => go("sites") : undefined}
            sharedLabels={sharedLabels}
          />
        ) : (
          <Button block leadingIcon={<icons.IconPlus size={16} />} onClick={() => go("sites")}>
            Anlage anlegen
          </Button>
        )
      }
      title={detailHead?.title ?? meta.title}
      /**
       * **Der Weg zurück steht neben dem Titel, den er verlässt** — nicht mehr
       * im Inhalt darunter. Beim Scrollen einer langen Detailseite wanderte er
       * sonst weg, und der Kopf hätte keinen Ausgang mehr.
       */
      back={
        currentSite === null
          ? undefined
          : openMeter !== null
            ? backTo("meters", "Zähler", currentSite.id)
            : openUnit !== null
              ? backTo("units", "Wohneinheiten", currentSite.id)
            : openAsset !== null
              ? backTo("generation", "Erzeugungsanlagen", currentSite.id)
            : deviceHead !== null
              ? backTo("devices", "Geräteverwaltung", currentSite.id)
              : undefined
      }
      /**
       * Im Kit steht hier `<Badge tone="success" dot>Live</Badge>` — fest
       * verdrahtet. „Live" wäre heute eine Behauptung: Es fließen keine
       * Werte. An derselben Stelle steht deshalb nur noch die Störung — hakt
       * es an der API, sagt es die Marke; sonst bleibt der Kopf ruhig.
       */
      /**
       * **Keine Verbindungsmarke auf einer Rechtsseite.** Das Kit setzt dort
       * `live={false}`, und aus gutem Grund: Die Marke sagt, ob die Anwendung
       * ihre API erreicht — ein Impressum steht auch dann vollständig da, wenn
       * sie es nicht tut. Eine rote Marke über einem Text, der nichts nachlädt,
       * ist eine Warnung ohne Gegenstand.
       */
      titleBadge={
        legalDocument === null ? <ConnectionBadge state={connectionState(health)} /> : undefined
      }
      /**
       * Im Kit: `activeSite.name + ' · ' + m.subtitle`. Hier nur, wo es zutrifft
       * — ein Konto-Bereich gehört keiner einzelnen Anlage, und der Name davor
       * behauptete einen Bezug, den es nicht gibt.
       */
      subtitle={
        /* **Und ein geöffneter Zähler nennt keine Anlage.** Seine Unterzeile
           trägt Bauart, Nummer und Zählwerke — die Anlage steht eine Ebene
           höher im Umschalter und wäre hier das vierte Glied einer Kette, die
           schon jetzt an einem Telefon umbricht. */
        detailHead !== null
          ? detailHead.subtitle
          : /* Eine Rechtsseite gehört keiner Anlage — der Name davor behauptete
               einen Bezug, den es nicht gibt, und zwar den irreführendsten von
               allen: Ein Impressum gilt nicht je Haus. */
            legalDocument !== null || !isSiteSection(area) || currentSite === null
            ? meta.subtitle
            : `${currentSite.name} · ${meta.subtitle}`
      }
      /**
       * **Ohne Anlage ist die Navigation leer.** Jeder Eintrag darin führt zu
       * einer Ansicht auf die gewählte Anlage; gibt es keine, führen alle vier
       * ins Nichts. Sie stehen zu lassen hieße, vier Wege anzubieten, die alle
       * an derselben leeren Stelle enden — und die Frage „was mache ich hier
       * falsch?" zu erzeugen. Stattdessen steht oben der eine Weg, der
       * weiterführt.
       */
      nav={
        adminView ? (
          <>
            {/* **Der Weg zurück steht als Eintrag in der Leiste**, nicht nur im
              Kopf darüber: Auf der Schiene fällt der Kopf weg, und ohne den
              Eintrag führte der einzige Weg zurück über das Kontomenü. Die
              gewählte Anlage gilt weiter (`?anlage=`) — zurück geht es auf
              ihre Startseite. */}
            <NavItem
              icon={<icons.IconArrowLeft size={18} />}
              label={currentSite === null ? "Zurück" : `Zurück zu ${currentSite.name}`}
              active={false}
              onClick={() => go("electricity")}
            >
              {currentSite === null ? "Zurück" : `Zurück zu ${currentSite.name}`}
            </NavItem>
            <NavSection>Administration</NavSection>
            <NavItem
              icon={<icons.IconActivity size={18} />}
              label="System"
              active={navArea === "system"}
              onClick={() => go("system")}
            >
              System
            </NavItem>
            <NavItem
              icon={<icons.IconUsers size={18} />}
              label="Anlagen und Nutzer"
              active={navArea === "admin"}
              onClick={() => go("admin")}
            >
              Anlagen und Nutzer
            </NavItem>
            <NavItem
              icon={<icons.IconDevice size={18} />}
              label="Geräte"
              active={navArea === "fleet"}
              onClick={() => go("fleet")}
            >
              Geräte
            </NavItem>
            <NavItem
              icon={<icons.IconGauge size={18} />}
              label="Nutzung"
              active={navArea === "usage"}
              onClick={() => go("usage")}
            >
              Nutzung
            </NavItem>
            <NavItem
              icon={<icons.IconBug size={18} />}
              label="Feedback"
              active={navArea === "feedback"}
              onClick={() => go("feedback")}
            >
              Feedback
            </NavItem>
          </>
        ) : sidebarUnitId !== null ? (
          /* **Die Leiste gehört der gewählten Wohnung** (HM-082), wie sonst der
             Anlage: Was darin steht, bezieht sich auf das, was oben gewählt ist.
             Ein Bewohner hat je Sparte eine Seite (HM-083) — heute nur Strom, und
             der ist sein Anfang, seit die Übersicht entfallen ist (HM-156);
             Zähler, Messkonzept und Geräte gehören dem Vermieter. */
          <>
            {/* Je Sparte eine Seite (HM-083); Wärme und Wasser kommen als Zeilen dazu. */}
            <NavItem
              icon={<icons.IconBolt size={18} />}
              label="Strom"
              active={residentUnit !== null}
              onClick={() => navigate({ kind: "resident-unit", unitId: sidebarUnitId, section: "electricity", period: residentUnit?.period ?? "day", date: residentUnit?.date ?? null })}
            >
              Strom
            </NavItem>
            {showUnitCharging && (
              <NavItem
                icon={<icons.IconPlug size={18} />}
                label="Laden"
                active={residentCharging !== null}
                onClick={() => navigate({ kind: "resident-charging", unitId: sidebarUnitId, chargePointId: null })}
              >
                Laden
              </NavItem>
            )}
          </>
        ) : residentOnly || !hasSite ? null : (
          <>
            {/* **`label` an jedem Eintrag, obwohl der Text darunter steht.**
              Eingeklappt bleibt vom Eintrag nur das Symbol, und der Name wird
              zur `aria-label`. `NavItem` liest ihn notfalls aus `children` —
              aber nur, solange das ein reiner Text ist. Wer hier eines Tages
              etwas Zusammengesetztes hineinschreibt, hätte sonst einen
              namenlosen Knopf, und das fiele erst jemandem mit Sprachausgabe
              auf. */}
            {/* **Der erste Eintrag ist die Startseite** (HM-156) — die Übersicht
              davor ist entfallen, wie im Entwurf. */}
            <NavItem
              icon={<icons.IconBolt size={18} />}
              label="Strom & Erzeugung"
              active={navArea === "electricity"}
              onClick={() => go("electricity")}
            >
              Strom &amp; Erzeugung
            </NavItem>
            {/* **Laden steht neben Strom und nicht bei den Geräten** (HM-119): Es ist
              täglicher Gebrauch, keine Verwaltung. Nur, wo es einen Ladepunkt gibt. */}
            {showCharging && (
              <NavItem
                icon={<icons.IconPlug size={18} />}
                label="Laden"
                active={navArea === "charging"}
                onClick={() => go("charging")}
              >
                Laden
              </NavItem>
            )}

            {/* Im Kit heißt dieser Abschnitt „System". Der Name ist seit dem
              Kontomenü vergeben — dort steht der Zustand der Dienste, und zwei
              Einträge mit demselben Wort meinten Verschiedenes. Was hier steht,
              ist ohnehin genauer: Man richtet die Anlage ein, statt sie
              anzusehen. */}
            <NavSection>Verwaltung</NavSection>

            {/* **Unter „Verwaltung", wie im Kit — und vor den Geräten.** Der
              Bereich verwaltet Stammdaten: Zählernummer, Bauart, Quelle je
              Zählwerk. Er zeigt keine Werte; die kommen mit HM-010 unter „Strom
              & Erzeugung". Vor der Geräteverwaltung, weil ein Zähler das ist,
              was gemessen wird, und ein Gerät nur die Quelle — dieselbe
              Reihenfolge wie auf der Detailseite eines Zählers. */}
            <NavItem
              icon={<icons.IconGauge size={18} />}
              label="Zähler"
              active={navArea === "meters"}
              onClick={() => go("meters")}
            >
              Zähler
            </NavItem>

            {/* **Zwischen Zählern und Messkonzept** (HM-079): Eine Wohneinheit
              sagt, wem eine Messstelle zuzurechnen ist; das Messkonzept steckt
              daraus seine Steckplätze (HM-080). Nur an einem Mehrfamilienhaus
              oder wo es schon Einheiten gibt — siehe `showUnits`. */}
            {showUnits && (
              <NavItem
                icon={<icons.IconDoor size={18} />}
                label="Wohneinheiten"
                active={navArea === "units"}
                onClick={() => go("units")}
              >
                Wohneinheiten
              </NavItem>
            )}

            {/* **Zwischen Wohneinheiten und Messkonzept** (HM-122, ADR 0038): Eine
              Erzeugungsanlage sagt, *was* hinter einem Erzeugungszähler steht —
              PV-Anlage, Speicher —, und das Messkonzept zeigt im Bild darauf.
              Immer sichtbar: Jedes Haus kann eine PV-Anlage haben, und eine
              Anlage ohne Zähler (Balkonkraftwerk) ist trotzdem eine. */}
            <NavItem
              icon={<icons.IconSun size={18} />}
              label="Erzeugungsanlagen"
              active={navArea === "generation"}
              onClick={() => go("generation")}
            >
              Erzeugungsanlagen
            </NavItem>

            {/* **Die Zahl zählt Geräte** (HM-050). Sie war Geräte plus
              Integrationen, und bei einem Lesekopf und der Cloud-Anbindung stand
              dort `2` — wer das zweite Gerät suchte, fand die Integration.
              Nebenbei entfällt damit, dass die beiden Summanden verschieden alt
              waren: `useDevices` lädt jede Minute nach, `useIntegrations` nicht — die
              der Rahmen deshalb gar nicht mehr holt. */}
            {/* **Nach den Zählern, vor den Geräten** — wie im Kit (HM-012). Das
              Messkonzept steckt Zähler in Rollen; es setzt die Zähler voraus und
              nicht die Geräte, und wer von oben liest, trifft erst das, was
              gemessen wird, dann, wie daraus gerechnet wird, dann die Quelle. */}
            <NavItem
              icon={<icons.IconTemplate size={18} />}
              label="Messkonzept"
              active={navArea === "concept"}
              onClick={() => go("concept")}
            >
              Messkonzept
            </NavItem>
            <NavItem
              icon={<icons.IconDevice size={18} />}
              label="Geräteverwaltung"
              badge={countOrNothing(deviceCount)}
              active={navArea === "devices"}
              onClick={() => go("devices")}
            >
              Geräteverwaltung
            </NavItem>
            <NavItem
              icon={<icons.IconGear size={18} />}
              label="Einstellungen"
              active={navArea === "settings"}
              onClick={() => go("settings")}
            >
              Einstellungen
            </NavItem>
          </>
        )
      }
      /**
       * **Zeitraum und Datum stehen in der Kopfleiste** — wie im Kit
       * (`TopBar.jsx`), und nur dort, wo eine Ansicht ihn auch einstellt:
       * „Strom & Erzeugung" und die Wohnung; ein Zeitraum an einer
       * Geräteliste hätte nichts einzustellen. Die Steuerung kennt die Zeitzone
       * der Anlage nicht; was ein Tag ist und wo heute liegt, sagt ihr der
       * Server über dieselbe Abfrage, die die Seite darunter stellt (HM-076).
       */
      headerRight={
        area === "electricity" && currentSite !== null && energyView !== null ? (
          <EnergyPeriodHeader
            siteId={currentSite.id}
            timeZone={currentSite.timezone}
            period={energyView.period}
            date={energyView.date}
          />
        ) : /* „Strom" einer Wohneinheit hat denselben Zeitraum-Wähler (HM-080):
             derselbe Kalenderzeitraum der Anlage, dieselbe Adresse. */
        area === "units" && currentSite !== null && openUnit !== null && openUnit.tab === "electricity" ? (
          <UnitPeriodHeader unit={openUnit} timeZone={currentSite.timezone} />
        ) : residentUnit !== null && residentUnitData !== null ? (
          <ResidentPeriodHeader unit={residentUnit} timeZone={residentUnitData.siteTimezone} />
        ) : undefined
      }
      navFooter={
        <>
          <AccountMenu
            /* Der Name, sofern einer hinterlegt ist — sonst die E-Mail-Adresse.
             Beide Namensfelder sind freiwillig (HM-033). */
            name={displayName(user)}
            detail={platformRoleLabel(user.platformRole)}
            avatarUpdatedAt={user.avatarUpdatedAt}
            initials={initialsOf(user)}
            activeKey={
              adminView ? "administration" : navArea === null || isSiteSection(navArea) ? null : navArea
            }
            entries={accountEntries}
            /**
             * Das Erscheinungsbild gehört dem, der vor dem Bildschirm sitzt —
             * wie das Profil, und anders als alles in der Leiste darüber
             * (HM-107). Im Menü steht es zwischen den Bereichen und „Abmelden".
             */
            appearance={<AppearancePicker />}
          />
          <LegalFooterLinks current={legalDocument ?? undefined} />
        </>
      }
      /**
       * Im Kit stehen hier Zeitraum-Umschalter, Datumsnavigator und die
       * Primäraktion. Die ersten beiden kommen mit HM-010 — ohne Daten hätten
       * sie nichts zu filtern. Bis dahin bleibt die Ecke leer; „Abmelden" ist
       * seit dem Kontomenü dort zu Hause, wo auch das Konto steht, und ein
       * Knopf nur zum Füllen wäre der falsche Grund.
       */
    >
      {/*
        **Die Rechtsseite steht vor den Bereichen und schließt sie aus.** Sie
        gehört keinem — ohne diesen Vorrang stünde unter ihr „Strom & Erzeugung",
        weil `area` für alles Undeutbare dorthin zeigt.
      */}
      {legalDocument !== null && <LegalDocumentScreen document={legalDocument} />}
      {legalDocument === null && residentUnit !== null && (
        <ResidentUnitScreen unitId={residentUnit.unitId} period={residentUnit.period} date={residentUnit.date} />
      )}
      {legalDocument === null && residentCharging !== null && (
        <ResidentChargingScreen unitId={residentCharging.unitId} chargePointId={residentCharging.chargePointId} />
      )}
      {/* **Ein reiner Bewohner ohne Wohnungsadresse landet in seiner ersten
          Wohnung — aber nur, wenn die Adresse einen Anlagen-Bereich meint.** Die
          Konto-Bereiche (Profil, Abmelden) gehören ihm wie jedem anderen; wer
          hier für jede Adresse `ResidentHome` zeigt, schickt ihn beim Klick auf
          „Profil" im selben Augenblick zurück in die Wohnung (HM-084). */}
      {legalDocument === null && residentUnitId === null && residentOnly && isSiteSection(area) && (
        <ResidentHome units={units} pending={myUnits.isPending} />
      )}
      {legalDocument === null && residentUnitId === null && (!residentOnly || !isSiteSection(area)) && (
        <>
      {area === "electricity" && (
        <ElectricityScreen
          siteId={currentSite?.id ?? null}
          siteTimeZone={currentSite?.timezone ?? "Europe/Berlin"}
          period={energyView?.period ?? "day"}
          date={energyView?.date ?? null}
        />
      )}
      {area === "charging" && currentSite !== null && (
        <ChargingScreen siteId={currentSite.id} chargePointId={openCharging?.chargePointId ?? null} />
      )}
      {area === "concept" && (
        <ConceptScreen
          /* Je Anlage ein eigener Editor (HM-190): Ein offenes Panel oder eine
             Zählerwahl aus Anlage A darf in Anlage B nichts speichern. */
          key={currentSite?.id ?? "none"}
          siteId={currentSite?.id ?? null}
          siteTimeZone={currentSite?.timezone ?? "Europe/Berlin"}
        />
      )}
      {/* Drei Ebenen in einem Bereich: die Liste, ein Gerät, ein Datenpunkt. Welche
          gilt, steht in der Adresse — die Seitenleiste zeigt in allen dreien
          „Geräteverwaltung", weil man den Bereich nicht verlassen hat. */}
      {area === "devices" && openPoint !== null && currentSite !== null && (
        <DatapointDetailScreen
          siteId={currentSite.id}
          siteTimeZone={currentSite.timezone}
          deviceId={openPoint.deviceId}
          datapointId={openPoint.datapointId}
          range={openPoint.range}
          onBack={() =>
            navigate({
              kind: "device",
              siteId: currentSite.id,
              deviceId: openPoint.deviceId,
              tab: "overview",
            })
          }
        />
      )}
      {area === "devices" &&
        openPoint === null &&
        (openDeviceId !== null && currentSite !== null ? (
          <DeviceDetailScreen
            siteId={currentSite.id}
            siteName={currentSite.name}
            deviceId={openDeviceId}
            tab={openDeviceTab}
            onBack={() => navigate(routeFor("devices", currentSite.id))}
          />
        ) : (
          <DeviceManagementScreen
            siteId={currentSite?.id ?? null}
            initialClaimCode={claimCode}
            onClaimCodeUsed={() => setClaimCode(null)}
          />
        ))}
      {/* Zwei Ebenen im Zähler-Bereich: die Liste und ein einzelner Zähler. */}
      {area === "meters" &&
        (openMeter !== null && currentSite !== null ? (
          <MeterDetailScreen
            siteId={currentSite.id}
            meterId={openMeter.meterId}
            tab={openMeter.tab}
            registerId={openMeter.register}
            range={openMeter.range}
            siteTimeZone={currentSite.timezone}
          />
        ) : (
          <MetersScreen siteId={currentSite?.id ?? null} />
        ))}
      {/* Zwei Ebenen im Wohneinheiten-Bereich: die Liste und eine Einheit. */}
      {area === "units" &&
        (openUnit !== null && currentSite !== null ? (
          <UnitDetailScreen
            siteId={currentSite.id}
            unitId={openUnit.unitId}
            tab={openUnit.tab}
            period={openUnit.period}
            date={openUnit.date}
            siteTimeZone={currentSite.timezone}
          />
        ) : (
          <UnitsScreen siteId={currentSite?.id ?? null} />
        ))}
      {/* Zwei Ebenen im Bereich Erzeugungsanlagen: die Liste und eine Anlage (HM-131). */}
      {area === "generation" &&
        (openAsset !== null && currentSite !== null ? (
          <GenerationAssetDetailScreen
            siteId={currentSite.id}
            assetId={openAsset.assetId}
            range={openAsset.range}
            siteTimeZone={currentSite.timezone}
          />
        ) : (
          <GenerationAssetsScreen siteId={currentSite?.id ?? null} />
        ))}
      {area === "settings" && (
        <SettingsScreen site={currentSite} onOpenDevices={() => go("devices")} />
      )}
      {area === "profile" && <ProfileScreen user={user} />}
      {area === "sites" && (
        /* Nach der ersten Anlage weiter zu „Strom & Erzeugung" — sie ist der Grund,
           warum man sie angelegt hat. Wer schon eine hatte, bleibt stehen.

           Die Adresse wird hier von Hand gebildet und nicht über `go`: Die neue
           Anlage steht noch in keiner Liste, `currentSite` ist also weiterhin
           `null`, und `go("electricity")` führte damit zurück auf ebendiese Liste. */
        <SitesCard
          user={user}
          onCreated={
            hasSite
              ? undefined
              : (site) => navigate({ kind: "electricity", siteId: site.id, period: "day", date: null })
          }
        />
      )}
      {area === "system" && isAdmin && <SystemScreen />}
      {/* Anlagen zuerst (HM-161): Die Frage, mit der man hierherkommt, lautet meist
          „wer sieht diese Anlage?" und erst danach „welche Rolle hat dieser Mensch?". */}
      {area === "admin" && isAdmin && <AdminSitesCard />}
      {area === "admin" && isAdmin && <AdminCard user={user} />}
      {area === "fleet" && isAdmin && openFleet !== null && <FleetScreen route={openFleet} />}
      {area === "usage" && isAdmin && openUsage !== null && <UsageScreen route={openUsage} />}
      {area === "feedback" && isAdmin && openFeedback !== null && <FeedbackScreen route={openFeedback} />}
        </>
      )}
    </AppShell>
    {/* Außerhalb des Gerüsts: Ist auf einem Telefon die Schublade offen, ist der
        Inhalt `inert` — ein Dialog darin nähme keine Eingabe an. */}
    {news !== null && legalDocument === null && (
      <NewsDialog
        entry={news}
        onDone={() => setNewsClosed(true)}
        onOpen={(target) => {
          if (target === "send-feedback") setFeedbackOpen(true);
        }}
      />
    )}
    <FeedbackDialog
      open={feedbackOpen}
      onClose={() => setFeedbackOpen(false)}
      page={usageScreenOf(route)}
      site={
        legalDocument === null && residentUnitId === null && currentSite !== null
          ? { id: currentSite.id, name: currentSite.name }
          : null
      }
    />
    </SiteAccessContext.Provider>
  );
}

type Connection = "pending" | "connected" | "degraded" | "lost";

/**
 * Ob die Anwendung ihre API erreicht.
 *
 * `503` vom Health-Endpunkt ist **kein** Verbindungsverlust: Der Dienst
 * antwortet ja — er meldet nur, dass eine seiner Prüfungen hakt. Erst wenn gar
 * keine Antwort kommt, ist die Verbindung weg.
 */
function connectionState(health: ReturnType<typeof useHealth>): Connection {
  if (health.isError) return "lost";
  if (health.data === undefined) return "pending";
  if (health.data.status === "ok") return "connected";
  return "degraded";
}

/**
 * Gesagt wird nur die Störung.
 *
 * „Verbunden" stand über **jeder** Seite und sagte dort nichts: Wer die Seite
 * samt ihrer Inhalte vor sich hat, erreicht seine API offensichtlich. In der
 * Geräteverwaltung sagte es sogar das Falsche — daneben steht die
 * Erreichbarkeit der Geräte und darüber der Zustand der Annahmestelle
 * (HM-056), und eine dritte grüne Marke im Kopf las sich als Aussage über
 * ebendiese. Bleibt der Zustand ungeklärt, steht dort ebenfalls nichts: Eine
 * Marke, die bei jedem Seitenaufruf kurz aufblitzt, ist schlimmer als keine.
 *
 * Und die beiden verbliebenen Marken benennen ihren Gegenstand. „Nicht
 * erreichbar" allein wäre an einer Geräteliste wieder die Verwechslung, die
 * hier gerade beseitigt wird.
 */
const CONNECTION_BADGES: Partial<
  Record<Connection, { tone: "success" | "warning" | "danger" | "neutral"; label: string }>
> = {
  degraded: { tone: "warning", label: "Dienst eingeschränkt" },
  lost: { tone: "danger", label: "Server nicht erreichbar" },
};

/**
 * Der Kopf der Leiste im Admin-Bereich — an der Stelle des Umschalters.
 *
 * **Ein ruhiger Kopf und kein Menü**: Er sagt, worauf sich die Einträge darunter
 * beziehen — die Installation, nicht eine Anlage —, und es gibt nichts zu
 * wählen. Maße und Flächen wie der ruhige Kopf des `SiteSwitcher`, damit der
 * Wechsel in den Admin-Bereich nicht aussieht wie eine andere Anwendung.
 */
function AdminHead() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        padding: "9px 10px",
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--border-subtle)",
        background: "var(--surface-inset)",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: 9,
          background: "var(--hm-green-50)",
          color: "var(--hm-green-600)",
        }}
      >
        <icons.IconShield size={18} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>
          Administration
        </span>
        <span style={{ display: "block", fontSize: 11.5, color: "var(--text-muted)" }}>
          Die ganze Installation
        </span>
      </span>
    </div>
  );
}

function ConnectionBadge({ state }: { state: Connection }) {
  const badge = CONNECTION_BADGES[state];
  if (badge === undefined) return null;
  return <Badge tone={badge.tone}>{badge.label}</Badge>;
}

/**
 * Der Weg von einer Detailebene zurück auf ihre Liste — Adresse und Rückruf aus
 * derselben Route gebildet, damit beide nicht auseinanderlaufen können.
 */
function backTo(area: Area, label: string, siteId: string) {
  const route = routeFor(area, siteId);
  return { href: routePath(route), label, onNavigate: () => navigate(route) };
}

/**
 * Die Zeitraumsteuerung der Kopfleiste. Sie stellt dieselbe Abfrage wie die
 * Seite darunter — derselbe Schlüssel, derselbe Eintrag im Zwischenspeicher —
 * und liest daraus, was der Server über den Zeitraum sagt.
 */
function EnergyPeriodHeader({
  siteId,
  timeZone,
  period,
  date,
}: {
  siteId: string;
  timeZone: string;
  period: EnergyPeriod;
  date: string | null;
}) {
  const energy = useSiteEnergy(siteId, { period, date });
  return (
    <PeriodControls
      period={period}
      date={date}
      info={energy.data?.period ?? null}
      timeZone={timeZone}
      onChange={(nextPeriod, nextDate) =>
        navigate({ kind: "electricity", siteId, period: nextPeriod, date: nextDate })
      }
    />
  );
}

/** Die Wohnung eines Bewohners — dieselbe Steuerung, dritte Adresse (HM-081). */
function ResidentPeriodHeader({
  unit,
  timeZone,
}: {
  unit: Extract<Route, { kind: "resident-unit" }>;
  timeZone: string;
}) {
  const energy = useUnitEnergy(unit.unitId, { period: unit.period, date: unit.date });
  return (
    <PeriodControls
      period={unit.period}
      date={unit.date}
      info={energy.data?.period ?? null}
      timeZone={timeZone}
      onChange={(nextPeriod, nextDate) => navigate({ ...unit, period: nextPeriod, date: nextDate })}
    />
  );
}

/**
 * Der Anfang eines reinen Bewohners ohne Adresse: Die **erste** Wohnung wird
 * geöffnet — die übrigen stehen oben im Umschalter, wie die Anlagen eines
 * Besitzers (HM-082). Erst, wenn die Liste da ist: Ein `replace` vor der Liste
 * liefe ins Leere.
 */
function ResidentHome({ units, pending }: { units: readonly { siteAreaId: string }[]; pending: boolean }) {
  const first = units[0]?.siteAreaId ?? null;
  useEffect(() => {
    if (first !== null) navigate({ kind: "resident-unit", unitId: first, section: "electricity", period: "day", date: null }, { replace: true });
  }, [first]);
  if (pending || first !== null) return null;
  return (
    <Card>
      <EmptyState title="Noch keine Wohnung" icon={<icons.IconDoor size={30} />}>
        Dein Konto ist mit keiner Wohnung verbunden. Eine Einladung des Vermieters führt hierher.
      </EmptyState>
    </Card>
  );
}

/** Dasselbe für „Strom" einer Wohneinheit — die Adresse behält Anlage, Einheit und Reiter. */
function UnitPeriodHeader({
  unit,
  timeZone,
}: {
  unit: Extract<Route, { kind: "unit" }>;
  timeZone: string;
}) {
  const energy = useUnitEnergy(unit.unitId, { period: unit.period, date: unit.date });
  return (
    <PeriodControls
      period={unit.period}
      date={unit.date}
      info={energy.data?.period ?? null}
      timeZone={timeZone}
      onChange={(nextPeriod, nextDate) => navigate({ ...unit, period: nextPeriod, date: nextDate })}
    />
  );
}

/** Eine Null wird nicht als Zahl an den Navigationseintrag geschrieben. */
function countOrNothing(count: number): string | undefined {
  return count === 0 ? undefined : count.toLocaleString("de-DE");
}

